// src/Home.js
import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./services/firebase";

import "./Home.css";
import aiStickPlacement from "./assets/ai-stick-placement.png";
import AuthForm from "./components/AuthForm";
import {
  FaTools,
  FaCamera,
  FaCheckCircle,
  FaClock,
  FaCalculator,
  FaHome,
  FaHardHat,
  FaShieldAlt,
  FaBolt,
  FaRulerCombined,
} from "react-icons/fa";

function Home() {
  const navigate = useNavigate();
  const ENV = "prod";
  const aiStickPDF = "/ai-stick/ai-stick-v2-prod.pdf";
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  const handleScrollToGetStarted = () => {
    const section = document.getElementById("get-started-section");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* ── Top bar ── */}
      <header className="public-topbar">
        <div className="public-topbar-inner">
          <Link to="/" className="public-brand">
            <img src="/logo.png" alt="MeasureSmart AI" className="public-brand-logo" />
          </Link>

          <nav className="public-navlinks">
            {user ? (
              <>
                <NavLink to="/">Home</NavLink>
                <NavLink to="/surface-mode">Surface Mode</NavLink>
                <NavLink to="/multiroom">Multi-Room</NavLink>
                <NavLink to="/contractor">Contractor</NavLink>
                <NavLink to="/admin">Admin</NavLink>
              </>
            ) : (
              <button className="topbar-signin-btn" onClick={handleScrollToGetStarted}>
                Sign In
              </button>
            )}
          </nav>
        </div>
      </header>

      <div className="home-container">
        {ENV === "staging" && <div className="env-banner">STAGING ENVIRONMENT</div>}

        {/* ── HERO ── */}
        <header className="hero-section">
          <div className="hero-inner">
            <p className="app-tagline-small">AI-powered surface &amp; room measurement</p>
            <h1 className="app-title">MeasureSmart AI</h1>
            <p className="tagline">
              Measure walls, backsplashes, and full rooms from a photo — using your
              11&quot; AI Stick as a calibration reference.{" "}
              <strong>No tape measure required.</strong>
            </p>

            {/* ── AHA MOMENT ── */}
            <div className="hero-aha">
              <span>
                <strong>The AI Stick is your ruler.</strong> Print it, place it in your photo,
                and the AI measures everything automatically.
              </span>
            </div>

            <p className="patent-pending">Patent Pending Technology</p>

            <div className="hero-cta-row">
              {!user ? (
                <button className="primary-btn hero-primary" onClick={handleScrollToGetStarted}>
                  Try the App — Free for Homeowners
                </button>
              ) : (
                <>
                  <button className="primary-btn hero-primary" onClick={() => navigate("/measure")}>
                    Select Measure Mode
                  </button>
                  <button className="secondary-btn" onClick={() => navigate("/surface-mode")}>
                    Surface Mode
                  </button>
                  <button className="secondary-btn" onClick={() => navigate("/multiroom")}>
                    Multi-Room Analyzer
                  </button>
                </>
              )}
              <a href={aiStickPDF} className="secondary-btn" target="_blank" rel="noopener noreferrer">
                Download AI Stick v2 (PDF)
              </a>
            </div>

            <p className="hero-subnote">
              New: <strong>Surface Mode (Beta)</strong> with Tap-to-Measure and AI-assisted stick detection.
            </p>
          </div>
        </header>

        {/* ── TRUST BAR ── */}
        <div className="trust-bar">
          <div className="trust-bar-inner">
            <div className="trust-item">
              <FaShieldAlt className="trust-icon" />
              <span>Patent Pending</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <FaBolt className="trust-icon" />
              <span>AI-Powered Calibration</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <FaHome className="trust-icon" />
              <span>Built for Homeowners</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <FaHardHat className="trust-icon" />
              <span>Trusted by Contractors</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <FaRulerCombined className="trust-icon" />
              <span>±2–4 Inch Accuracy</span>
            </div>
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <section className="content-section how-section">
          <h2>How It Works</h2>
          <p className="section-intro">Three simple steps from photo to measurement.</p>

          <div className="how-steps">
            <div className="how-step-card">
              <div className="how-step-num">1</div>
              <div className="how-step-icon"><FaTools /></div>
              <h3>Print the AI Stick</h3>
              <p>
                Download and print the 11&quot; AI Stick v2. Place it in the scene anywhere
                it's clearly visible and flat against the surface you want to measure.
              </p>
              <p className="how-step-hint">Your physical ruler for every photo.</p>
            </div>

            <div className="how-step-connector" />

            <div className="how-step-card">
              <div className="how-step-num">2</div>
              <div className="how-step-icon"><FaCamera /></div>
              <h3>Upload a Photo</h3>
              <p>
                Take a photo with the Stick visible. MeasureSmart AI detects the Stick
                and uses it as a reference scale for your entire image.
              </p>
              <p className="how-step-hint">One photo is all it takes.</p>
            </div>

            <div className="how-step-connector" />

            <div className="how-step-card">
              <div className="how-step-num">3</div>
              <div className="how-step-icon"><FaCheckCircle /></div>
              <h3>Tap to Measure</h3>
              <p>
                Tap two points to define width &amp; height. The app instantly converts
                pixels to real-world dimensions and shows square footage.
              </p>
              <p className="how-step-hint">Instant dimensions — no math needed.</p>
            </div>
          </div>
        </section>

        {/* ── STICK PLACEMENT ── */}
        <section className="content-section light-bg">
          <h2>Before You Start — Stick Placement</h2>
          <p className="section-intro">
            Place the AI Stick v2 flat against the surface and fully visible in your photo.
          </p>

          <div className="placement-layout">
            <div className="placement-image-wrapper">
              <img src={aiStickPlacement} alt="AI Stick placement" className="placement-image" />
              <p className="placement-caption">Print at 100% scale</p>
            </div>

            <ul className="placement-tips">
              <li>Keep the Stick straight.</li>
              <li>Avoid glare or deep shadows.</li>
              <li>Capture the entire work area.</li>
              <li>For windows, place the Stick near the frame.</li>
            </ul>
          </div>

          <div className="centered-cta-row">
            <a href={aiStickPDF} className="secondary-btn" target="_blank" rel="noopener noreferrer">
              Download AI Stick v2 (PDF)
            </a>
          </div>
        </section>

        {/* ── DEMO ── */}
        <section className="content-section">
          <h2>See Tap-to-Measure in Action</h2>
          <p className="section-intro">
            <strong>"How does the AI measure a room from a picture?"</strong>{" "}
            A quick walkthrough of how the AI Stick, your photo, and Tap-to-Measure work together.
          </p>

          <div className="demo-card">
            <div className="demo-card-inner">
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                style={{ width: "100%", borderRadius: "8px", display: "block" }}
              >
                <source
                  src="https://stmeasuresmart.blob.core.windows.net/landgpagevid/mesuresmartaidemowvoice.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
              <div className="demo-pill-row" style={{ marginTop: "1rem" }}>
                <span className="demo-pill">AI Stick calibration</span>
                <span className="demo-pill">Tap-to-Measure</span>
                <span className="demo-pill">Instant dimensions</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── WHY USE ── */}
        <section className="content-section light-bg">
          <h2>Why Use MeasureSmart AI?</h2>
          <div className="card-grid">
            <div className="info-card">
              <div className="info-icon-wrap blue"><FaCheckCircle className="info-icon" /></div>
              <h3>Accurate</h3>
              <p>AI-calibrated measurements with consistent ±2–4 inch precision.</p>
            </div>
            <div className="info-card">
              <div className="info-icon-wrap navy"><FaCalculator className="info-icon" /></div>
              <h3>Instant Dimensions</h3>
              <p>Get width, height, and square footage instantly — no manual math.</p>
            </div>
            <div className="info-card">
              <div className="info-icon-wrap teal"><FaClock className="info-icon" /></div>
              <h3>Time-Saving</h3>
              <p>No ladders, no repeated visits, no tape measure needed.</p>
            </div>
          </div>
        </section>

        {/* ── WHY IT MATTERS ── */}
        <section className="content-section">
          <h2>Why It Matters</h2>
          <p className="section-intro">
            Fast, transparent, data-driven measurements for homeowners &amp; contractors.
          </p>

          <div className="card-grid two-cards">
            <div className="info-card audience-card">
              <div className="audience-header">
                <div className="info-icon-wrap blue"><FaHome className="info-icon" /></div>
                <h3>For Homeowners</h3>
              </div>
              <ul className="bulleted-list">
                <li>Measure surfaces without tools.</li>
                <li>Share consistent dimensions.</li>
                <li>Compare project options easily.</li>
              </ul>
            </div>

            <div className="info-card audience-card">
              <div className="audience-header">
                <div className="info-icon-wrap navy"><FaHardHat className="info-icon" /></div>
                <h3>For Contractors</h3>
              </div>
              <ul className="bulleted-list">
                <li>Get photos + standardized measurements.</li>
                <li>Quote faster from remote requests.</li>
                <li>Build trust with AI-backed results.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── GET STARTED (dark CTA) ── */}
        <section className="cta-section" id="get-started-section">
          <div className="cta-inner">
            <h2 className="cta-title">Ready to Measure Smarter?</h2>
            <p className="cta-sub">
              Create a free account to upload photos and save your measurements.
              No credit card required.
            </p>
            <div className="auth-form-wrapper">
              <AuthForm />
            </div>
          </div>
        </section>

        <p className="contact-email">
          Questions?{" "}
          <a href="mailto:info@measuresmart.ai">info@measuresmart.ai</a>
        </p>

        <footer className="app-footer">
          <p>© {new Date().getFullYear()} MeasureSmart AI</p>
          <p className="footer-links">
            <Link to="/privacy">Privacy Policy</Link> ·{" "}
            <Link to="/terms">Terms of Use</Link>
          </p>
        </footer>
      </div>
    </>
  );
}

export default Home;
